import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Plus,
  Edit,
  Trash2,
  Clock,
  Users,
  Calendar,
  MessageSquare,
  Download,
  Eye,
  CreditCard,
  FileText,
  Check,
  CheckCircle2,
  DollarSign,
  Link,
  Unlink2,
  ArrowLeft,
  Archive,
  MoreHorizontal,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { toast } from "sonner";
import { EditClientSheet } from "./EditClientSheet";
import { AddLocationSheet } from "./AddLocationSheet";
import { EditLocationSheet } from "./EditLocationSheet";
import { AttachToLocationSheet } from "@/components/bids/AttachToLocationSheet";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { APIProvider } from "@vis.gl/react-google-maps";

// Feature flag to control billing tab visibility
// Set to false to hide billing tab from production
const SHOW_BILLING_TAB = false;

interface ServiceLocation {
  _id: Id<"serviceLocations">;
  name: string;
  address: string;
  squareFootage: string;
  cleaningFrequency: string;
  serviceDays: string[];
  staffRequired?: number;
  businessId: Id<"businesses">;
  orgId: Id<"organizations">;
  createdAt: number;
  updatedAt: number;
  contactName?: string;
  contactTitle?: string;
  contactEmail?: string;
  contactPhone?: string;
}

export default function ClientView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [isAddLocationSheetOpen, setIsAddLocationSheetOpen] = useState(false);
  const [isEditLocationSheetOpen, setIsEditLocationSheetOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<ServiceLocation | null>(null);
  const [isAttachToLocationSheetOpen, setIsAttachToLocationSheetOpen] = useState(false);
  const [selectedBidForAttachment, setSelectedBidForAttachment] = useState<any>(null);
  const [newNote, setNewNote] = useState("");
  const [activeTab, setActiveTab] = useState<"overview" | "bids" | "billing" | "leadHistory">(
    "overview"
  );

  // Redirect to overview if billing tab is accessed when hidden
  useEffect(() => {
    if (activeTab === "billing" && !SHOW_BILLING_TAB) {
      setActiveTab("overview");
    }
  }, [activeTab]);

  // Fetch business data from Convex
  const business = useQuery(
    api.businesses.getBusinessById,
    id ? { id: id as Id<"businesses"> } : "skip"
  );

  // Fetch locations for this business
  const locations =
    useQuery(
      api.serviceLocations.getLocationsByBusiness,
      id ? { businessId: id as Id<"businesses"> } : "skip"
    ) ?? [];

  // Fetch bids for this client
  const bids =
    useQuery(api.bids.getBidsByBusinessId, id ? { businessId: id as Id<"businesses"> } : "skip") ??
    [];

  // Fetch lead history data
  const leadTasks =
    useQuery(
      api.leadTasks.getTasksByBusiness,
      id ? { businessId: id as Id<"businesses"> } : "skip"
    ) ?? [];

  const leadNotes =
    useQuery(
      api.leadNotes.getNotesByBusiness,
      id ? { businessId: id as Id<"businesses"> } : "skip"
    ) ?? [];

  const leadDocuments =
    useQuery(
      api.businesses.getDocumentsByBusiness,
      id ? { businessId: id as Id<"businesses"> } : "skip"
    ) ?? [];

  // Mutations
  const createNote = useMutation(api.leadNotes.createNote);
  const updateBid = useMutation(api.bids.update);

  // Calculate service overview metrics
  const totalLocations = locations.length;
  const totalSquareFootage = locations.reduce((total: number, location: any) => {
    const sqft = parseInt(location.squareFootage.replace(/[^\d]/g, "")) || 0;
    return total + sqft;
  }, 0);
  const totalStaffRequired = locations.reduce((total: number, location: any) => {
    return total + (location.staffRequired || 0);
  }, 0);

  // Format square footage for display
  const formatSquareFootage = (sqft: number) => {
    return sqft.toLocaleString() + " sq ft";
  };

  // Frequency options (matching the service locations page)
  const FREQUENCY_OPTIONS = [
    { value: "1_per_week", label: "1 time per week" },
    { value: "2_per_week", label: "2 times per week" },
    { value: "3_per_week", label: "3 times per week" },
    { value: "4_per_week", label: "4 times per week" },
    { value: "5_per_week", label: "5 times per week" },
    { value: "6_per_week", label: "6 times per week" },
    { value: "7_per_week", label: "7 times per week" },
    { value: "1_per_month", label: "1 time per month" },
    { value: "2_per_month", label: "2 times per month" },
  ];

  // Format frequency for display using lookup
  const formatFrequency = (frequency: string) => {
    const freq = FREQUENCY_OPTIONS.find((f) => f.value === frequency);
    return freq ? freq.label : frequency;
  };

  const archiveClient = useMutation(api.businesses.archive);

  const handleArchiveClient = async () => {
    if (!id) return;
    try {
      await archiveClient({ id: id as Id<"businesses"> });
      toast.success(`${business?.name || "Client"} archived successfully`);
      navigate("/clients");
    } catch (error) {
      console.error("Failed to archive client:", error);
      toast.error("Failed to archive client");
    }
  };

  const archiveLocation = useMutation(api.serviceLocations.archive);

  const handleArchiveLocation = async (locationId: Id<"serviceLocations">, locationName: string) => {
    try {
      await archiveLocation({ id: locationId });
      toast.success(`${locationName} archived successfully`);
    } catch (error) {
      console.error("Failed to archive location:", error);
      toast.error("Failed to archive location");
    }
  };

  // Handle attach to location
  const handleAttachToLocation = (bid: any) => {
    setSelectedBidForAttachment(bid);
    setIsAttachToLocationSheetOpen(true);
  };

  // Handle unlink from location
  const handleUnlinkFromLocation = async (bid: any) => {
    try {
      await updateBid({
        id: bid._id,
        serviceLocationId: undefined,
      });
      toast.success(`"${bid.title}" unlinked from location`);
    } catch (error) {
      console.error("Error unlinking bid:", error);
      toast.error("Failed to unlink bid from location");
    }
  };

  // Handle adding notes
  const handleAddNote = async () => {
    if (!newNote.trim() || !id) return;

    try {
      await createNote({
        businessId: id as Id<"businesses">,
        content: newNote.trim(),
      });
      setNewNote("");
      toast.success("Note added successfully");
    } catch (error) {
      console.error("Error adding note:", error);
      toast.error("Failed to add note");
    }
  };

  // Loading state
  if (business === undefined) {
    return (
      <DashboardLayout activeTab="clients">
        <div className="flex h-64 items-center justify-center">
          <div className="text-gray-500">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  // Not found state
  if (business === null) {
    return (
      <DashboardLayout activeTab="clients">
        <div className="flex h-64 items-center justify-center">
          <div className="text-gray-500">Client not found</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ""}>
      <DashboardLayout activeTab="clients">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <div className="mb-2 flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1 pl-0"
                  onClick={() => navigate("/clients")}
                >
                  <ArrowLeft className="h-4 w-4" /> Back to Clients
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold text-gray-900">{business.name}</h1>
                {business.serviceStatus && (
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      {
                        active: "bg-green-100 text-green-700",
                        inactive: "bg-gray-100 text-gray-700",
                        pending: "bg-yellow-100 text-yellow-700",
                        suspended: "bg-red-100 text-red-700",
                      }[
                        business.serviceStatus as "active" | "inactive" | "pending" | "suspended"
                      ] || "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {business.serviceStatus.charAt(0).toUpperCase() +
                      business.serviceStatus.slice(1)}
                  </span>
                )}
              </div>
              <div className="mt-2 flex items-center gap-2 text-gray-500">
                <Building2 className="h-4 w-4" />
                <span>{business.type === "client" ? "Client" : "Lead"}</span>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="gap-2" onClick={handleArchiveClient}>
                <Archive className="h-4 w-4" /> Archive Client
              </Button>
              <Button className="gap-2" onClick={() => setIsEditSheetOpen(true)}>
                <Edit className="h-4 w-4" /> Edit Client
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex space-x-8">
              <button
                className={`border-b-2 px-1 py-4 text-sm font-medium ${
                  activeTab === "overview"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("overview")}
              >
                Overview
              </button>
              <button
                className={`border-b-2 px-1 py-4 text-sm font-medium ${
                  activeTab === "bids"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("bids")}
              >
                Bids
              </button>
              {SHOW_BILLING_TAB && (
                <button
                  className={`border-b-2 px-1 py-4 text-sm font-medium ${
                    activeTab === "billing"
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                  }`}
                  onClick={() => setActiveTab("billing")}
                >
                  Billing
                </button>
              )}
              <button
                className={`border-b-2 px-1 py-4 text-sm font-medium ${
                  activeTab === "leadHistory"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("leadHistory")}
              >
                Lead History
              </button>
            </div>
          </div>

          {activeTab === "overview" && (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {/* Left Column - Details */}
              <div className="space-y-6">
                <Card className="p-6">
                  <h2 className="mb-4 text-lg font-semibold">Client Information</h2>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                        <Building2 className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-medium">{business.name}</h3>
                        <p className="text-sm text-gray-500">
                          {business.address || "No address provided"}
                        </p>
                      </div>
                    </div>

                    <Separator />

                    <div className="space-y-3">
                      <h3 className="text-sm font-medium">Contact Information</h3>
                      <div className="space-y-2">
                        {business.contactName && (
                          <div className="flex items-center gap-2 text-gray-700">
                            <Users className="h-4 w-4 text-gray-400" />
                            <span>
                              {business.contactName}
                              {business.contactTitle && ` - ${business.contactTitle}`}
                            </span>
                          </div>
                        )}
                        {business.contactEmail && (
                          <div className="flex items-center gap-2 text-gray-700">
                            <Mail className="h-4 w-4 text-gray-400" />
                            <a
                              href={`mailto:${business.contactEmail}`}
                              className="text-blue-600 hover:underline"
                            >
                              {business.contactEmail}
                            </a>
                          </div>
                        )}
                        {business.contactPhone && (
                          <div className="flex items-center gap-2 text-gray-700">
                            <Phone className="h-4 w-4 text-gray-400" />
                            <span>{business.contactPhone}</span>
                          </div>
                        )}
                        {business.website && (
                          <div className="flex items-center gap-2 text-gray-700">
                            <Globe className="h-4 w-4 text-gray-400" />
                            <a
                              href={
                                business.website.startsWith("http")
                                  ? business.website
                                  : `https://${business.website}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              {business.website}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <h2 className="mb-4 text-lg font-semibold">Service Overview</h2>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Total Locations</span>
                      <span className="font-medium">{totalLocations}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Total Square Footage</span>
                      <span className="font-medium">{formatSquareFootage(totalSquareFootage)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Total Staff Required</span>
                      <span className="font-medium">{totalStaffRequired}</span>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Right Column - Service Locations */}
              <div className="md:col-span-2">
                <Card className="p-6">
                  <div className="mb-6 flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Service Locations</h2>
                    <div className="flex gap-2">
                      <Button variant="outline" className="gap-2" onClick={() => navigate(`/clients/${id}/locations/archived`)}>
                        <Archive className="h-4 w-4" /> Archived
                      </Button>
                      <Button className="gap-2" onClick={() => setIsAddLocationSheetOpen(true)}>
                        <Plus className="h-4 w-4" /> Add Location
                      </Button>
                    </div>
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Location</TableHead>
                        <TableHead>Square Footage</TableHead>
                        <TableHead>Frequency</TableHead>
                        <TableHead>Staff</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {locations.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <EmptyState
                              icon={MapPin}
                              title="No locations yet"
                              description="Add a location to get started."
                            />
                          </TableCell>
                        </TableRow>
                      ) : (
                        locations.map((location: any) => (
                          <TableRow key={location._id}>
                            <TableCell>
                              <div className="space-y-1">
                                <div className="font-medium">{location.name}</div>
                                <div className="text-sm text-gray-500">{location.address}</div>
                              </div>
                            </TableCell>
                            <TableCell>{location.squareFootage}</TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <Clock className="h-4 w-4 text-gray-500" />
                                  {formatFrequency(location.cleaningFrequency)}
                                </div>
                                <div className="mt-1 flex flex-wrap gap-1 text-xs text-gray-500">
                                  {location.serviceDays?.map((day: any) => (
                                    <span
                                      key={day}
                                      className="rounded bg-blue-50 px-1.5 py-0.5 text-blue-700"
                                    >
                                      {day.substring(0, 3)}
                                    </span>
                                  )) || "No days specified"}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-gray-500" />
                                <span>{location.staffRequired || 0} required</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => {
                                      sessionStorage.setItem("previousPage", `/clients/${id}`);
                                      navigate(`/clients/locations/${location._id}`);
                                    }}
                                  >
                                    <Eye className="mr-2 h-4 w-4" />
                                    View
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedLocation(location);
                                      setIsEditLocationSheetOpen(true);
                                    }}
                                  >
                                    <Edit className="mr-2 h-4 w-4" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleArchiveLocation(location._id, location.name)}
                                    className="text-red-600"
                                  >
                                    <Archive className="mr-2 h-4 w-4" />
                                    Archive
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            </div>
          )}

          {activeTab === "billing" && SHOW_BILLING_TAB && (
            <div className="space-y-6">
              {/* Billing Summary */}
              <Card className="p-6">
                <h2 className="mb-4 text-lg font-semibold">Billing Summary</h2>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Monthly Rate</span>
                      <span className="font-medium">$12,500.00</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Payment Status</span>
                      <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
                        Current
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Payment Terms</span>
                      <span className="font-medium">Net 30</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Next Invoice</span>
                      <span className="font-medium">June 1, 2024</span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Year-to-Date Billed</span>
                      <span className="font-medium">$62,500.00</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Year-to-Date Paid</span>
                      <span className="font-medium">$50,000.00</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Billing Cycle</span>
                      <span className="font-medium">Monthly (1st)</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Contract Renewal</span>
                      <span className="font-medium">Jan 15, 2025</span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Invoices by Location */}
              <Card className="p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Invoices by Location</h2>
                  <div className="flex items-center gap-2">
                    <select className="rounded border px-2 py-1 text-sm">
                      <option value="all">All Locations</option>
                      <option value="main">Main Office</option>
                      <option value="lab">Research Lab</option>
                      <option value="warehouse">Warehouse</option>
                    </select>
                    <Button variant="outline" size="sm" className="text-sm">
                      <Download className="mr-1 h-3.5 w-3.5" /> Export
                    </Button>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell>INV-2024-05</TableCell>
                      <TableCell>May 1, 2024</TableCell>
                      <TableCell>Main Office</TableCell>
                      <TableCell>$6,250.00</TableCell>
                      <TableCell>
                        <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
                          Paid
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <Mail className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>INV-2024-05</TableCell>
                      <TableCell>May 1, 2024</TableCell>
                      <TableCell>Research Lab</TableCell>
                      <TableCell>$4,375.00</TableCell>
                      <TableCell>
                        <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
                          Paid
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <Mail className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>INV-2024-05</TableCell>
                      <TableCell>May 1, 2024</TableCell>
                      <TableCell>Warehouse</TableCell>
                      <TableCell>$1,875.00</TableCell>
                      <TableCell>
                        <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
                          Paid
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <Mail className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Card>

              {/* Payment History */}
              <Card className="p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Payment History</h2>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className="text-sm">
                      <Calendar className="mr-1 h-3.5 w-3.5" /> Filter by Date
                    </Button>
                    <Button variant="outline" size="sm" className="text-sm">
                      <Download className="mr-1 h-3.5 w-3.5" /> Export
                    </Button>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Transaction ID</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Invoice #</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell>TRX-2024-05-001</TableCell>
                      <TableCell>May 5, 2024</TableCell>
                      <TableCell>$12,500.00</TableCell>
                      <TableCell>ACH Transfer</TableCell>
                      <TableCell>
                        <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
                          Reconciled
                        </span>
                      </TableCell>
                      <TableCell>INV-2024-05</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>TRX-2024-04-001</TableCell>
                      <TableCell>Apr 3, 2024</TableCell>
                      <TableCell>$12,500.00</TableCell>
                      <TableCell>ACH Transfer</TableCell>
                      <TableCell>
                        <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
                          Reconciled
                        </span>
                      </TableCell>
                      <TableCell>INV-2024-04</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>TRX-2024-03-001</TableCell>
                      <TableCell>Mar 5, 2024</TableCell>
                      <TableCell>$12,500.00</TableCell>
                      <TableCell>ACH Transfer</TableCell>
                      <TableCell>
                        <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
                          Reconciled
                        </span>
                      </TableCell>
                      <TableCell>INV-2024-03</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Card>

              {/* Billing Settings */}
              <Card className="p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Billing Settings</h2>
                  <Button variant="outline" size="sm">
                    <Edit className="mr-2 h-4 w-4" /> Edit Settings
                  </Button>
                </div>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-4">
                    <div>
                      <h3 className="mb-2 text-sm font-medium">Billing Preferences</h3>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <span>Email Invoices to:</span>
                        <span className="font-medium">
                          accounting@techcorp.com, john@techcorp.com
                        </span>
                      </div>
                    </div>
                    <div>
                      <h3 className="mb-2 text-sm font-medium">Payment Terms</h3>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span>Net 30 - Due within 30 days of invoice date</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="mb-2 text-sm font-medium">Payment Methods</h3>
                      <div className="flex items-center gap-2 text-gray-700">
                        <CreditCard className="h-4 w-4 text-gray-400" />
                        <span>ACH Transfer (Primary)</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <h3 className="mb-2 text-sm font-medium">Tax Information</h3>
                      <div className="flex items-center gap-2 text-gray-700">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span>Tax ID: XX-XXXXXXX</span>
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-gray-700">
                        <Check className="h-4 w-4 text-gray-400" />
                        <span>Tax Exempt: No</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="mb-2 text-sm font-medium">Billing Address</h3>
                      <div className="flex items-start gap-2 text-gray-700">
                        <MapPin className="mt-0.5 h-4 w-4 text-gray-400" />
                        <span>
                          TechCorp Solutions
                          <br />
                          Attn: Accounts Payable
                          <br />
                          123 Business Ave
                          <br />
                          San Francisco, CA 94105
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {activeTab === "bids" && (
            <div className="space-y-6">
              <Card className="p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Bids</h2>
                  <div className="text-sm text-gray-500">{bids.length} total bids</div>
                </div>

                {bids.length === 0 ? (
                  <EmptyState
                    icon={DollarSign}
                    title="No bids yet"
                    description="No bids have been created for this client."
                  />
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Title</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Location</TableHead>
                          <TableHead>Last Modified</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bids.map((bid: any) => (
                          <TableRow key={bid._id}>
                            <TableCell>
                              <div className="font-medium">{bid.title}</div>
                              {bid.description && (
                                <div className="mt-1 text-sm text-gray-500">
                                  {bid.description.length > 50
                                    ? `${bid.description.substring(0, 50)}...`
                                    : bid.description}
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                                  bid.status === "Approved"
                                    ? "bg-green-100 text-green-700"
                                    : bid.status === "Submitted"
                                      ? "bg-blue-100 text-blue-700"
                                      : bid.status === "In Review"
                                        ? "bg-yellow-100 text-yellow-700"
                                        : bid.status === "Rejected"
                                          ? "bg-red-100 text-red-700"
                                          : "bg-gray-100 text-gray-700"
                                }`}
                              >
                                {bid.status}
                              </span>
                            </TableCell>
                            <TableCell>
                              {bid.serviceLocationId ? (
                                <span className="text-sm text-gray-900">
                                  {locations.find((l: any) => l._id === bid.serviceLocationId)
                                    ?.name || "Unknown Location"}
                                </span>
                              ) : (
                                <span className="text-sm italic text-gray-400">Not attached</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-gray-500">
                                {new Date(bid.lastModified).toLocaleDateString()}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="gap-1"
                                  onClick={() =>
                                    navigate(`/bids/${bid._id}`, {
                                      state: { from: `/clients/${id}` },
                                    })
                                  }
                                >
                                  <Eye className="h-4 w-4" /> View
                                </Button>
                                {bid.serviceLocationId ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="gap-1 text-red-600 hover:bg-red-50 hover:text-red-700"
                                    onClick={() => handleUnlinkFromLocation(bid)}
                                  >
                                    <Unlink2 className="h-4 w-4" /> Unlink
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="gap-1"
                                    onClick={() => handleAttachToLocation(bid)}
                                  >
                                    <Link className="h-4 w-4" /> Attach to Location
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </Card>
            </div>
          )}

          {activeTab === "leadHistory" && (
            <div className="space-y-6">
              <Card className="p-6">
                <h2 className="mb-4 text-lg font-semibold">Lead History</h2>
                <Tabs defaultValue="tasks" className="w-full">
                  <TabsList className="mb-4">
                    <TabsTrigger value="tasks" className="gap-2">
                      <CheckCircle2 className="h-4 w-4" /> Tasks
                    </TabsTrigger>
                    <TabsTrigger value="notes" className="gap-2">
                      <MessageSquare className="h-4 w-4" /> Notes
                    </TabsTrigger>
                    <TabsTrigger value="activity" className="gap-2">
                      <Clock className="h-4 w-4" /> Activity
                    </TabsTrigger>
                    <TabsTrigger value="files" className="gap-2">
                      <FileText className="h-4 w-4" /> Files
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="tasks" className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Tasks</h3>
                      <Button size="sm" className="gap-2">
                        <Plus className="h-4 w-4" /> Add Task
                      </Button>
                    </div>
                    {leadTasks.length === 0 ? (
                      <EmptyState
                        icon={CheckCircle2}
                        title="No tasks yet"
                        description="No tasks have been created for this client's lead history."
                      />
                    ) : (
                      <ScrollArea className="h-[400px] pr-4">
                        <div className="space-y-4">
                          {leadTasks.map((task: any) => (
                            <Card key={task._id} className="p-4">
                              <div className="flex items-start justify-between">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2">
                                    <CheckCircle2
                                      className={`h-4 w-4 ${
                                        task.status === "completed"
                                          ? "text-green-500"
                                          : "text-gray-400"
                                      }`}
                                    />
                                    <span className="font-medium">{task.title}</span>
                                  </div>
                                  <div className="flex items-center gap-4 text-sm text-gray-500">
                                    <div className="flex items-center gap-1">
                                      <Calendar className="h-3 w-3" />
                                      {new Date(task.dueDate).toLocaleDateString()}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Users className="h-3 w-3" />
                                      {task.assignedToName || "Unassigned"}
                                    </div>
                                  </div>
                                </div>
                                <Button variant="ghost" size="sm">
                                  Edit
                                </Button>
                              </div>
                            </Card>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </TabsContent>

                  <TabsContent value="notes" className="space-y-4">
                    <div className="mb-4 flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Notes</h3>
                    </div>
                    <div className="space-y-4">
                      <Card className="p-4">
                        <Textarea
                          placeholder="Add a note..."
                          className="mb-4"
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                        />
                        <Button
                          className="gap-2"
                          onClick={handleAddNote}
                          disabled={!newNote.trim()}
                        >
                          <Plus className="h-4 w-4" /> Add Note
                        </Button>
                      </Card>
                      {leadNotes.length === 0 ? (
                        <EmptyState
                          icon={MessageSquare}
                          title="No notes yet"
                          description="Add notes to track important information about this client."
                        />
                      ) : (
                        <ScrollArea className="h-[300px] pr-4">
                          <div className="space-y-4">
                            {leadNotes.map((note: any) => (
                              <Card key={note._id} className="p-4">
                                <div className="space-y-2">
                                  <p className="text-gray-600">{note.content}</p>
                                  <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <Users className="h-3 w-3" />
                                    <span>{note.createdByName || "Unknown"}</span>
                                    <span>•</span>
                                    <span>{new Date(note.createdAt).toLocaleString()}</span>
                                  </div>
                                </div>
                              </Card>
                            ))}
                          </div>
                        </ScrollArea>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="activity" className="space-y-4">
                    <h3 className="mb-4 text-lg font-semibold">Activity Timeline</h3>
                    {(() => {
                      // Combine tasks and notes into activity timeline
                      const activities = [
                        ...leadTasks.map((task: any) => ({
                          id: task._id,
                          type: "task",
                          title: task.title,
                          date: new Date(task.createdAt),
                          user: task.assignedToName || "Unknown",
                          status: task.status,
                          icon: CheckCircle2,
                          iconColor:
                            task.status === "completed" ? "text-green-600" : "text-gray-600",
                          bgColor: task.status === "completed" ? "bg-green-100" : "bg-gray-100",
                        })),
                        ...leadNotes.map((note: any) => ({
                          id: note._id,
                          type: "note",
                          title: note.content,
                          date: new Date(note.createdAt),
                          user: note.createdByName || "Unknown",
                          icon: MessageSquare,
                          iconColor: "text-blue-600",
                          bgColor: "bg-blue-100",
                        })),
                      ].sort((a, b) => b.date.getTime() - a.date.getTime());

                      return activities.length === 0 ? (
                        <EmptyState
                          icon={Clock}
                          title="No activity yet"
                          description="Activity will appear here as tasks and notes are created."
                        />
                      ) : (
                        <ScrollArea className="h-[400px] pr-4">
                          <div className="space-y-4">
                            {activities.map((activity) => (
                              <Card key={activity.id} className="p-4">
                                <div className="flex items-start gap-4">
                                  <div className={`rounded-full p-2 ${activity.bgColor}`}>
                                    <activity.icon className={`h-4 w-4 ${activity.iconColor}`} />
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-medium">
                                      {activity.type === "task"
                                        ? `Task: ${activity.title}`
                                        : activity.title}
                                    </p>
                                    <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                                      <Users className="h-3 w-3" />
                                      <span>{activity.user}</span>
                                      <span>•</span>
                                      <span>{activity.date.toLocaleString()}</span>
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            ))}
                          </div>
                        </ScrollArea>
                      );
                    })()}
                  </TabsContent>

                  <TabsContent value="files" className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Files & Documents</h3>
                      <Button size="sm" className="gap-2">
                        <Plus className="h-4 w-4" /> Upload File
                      </Button>
                    </div>
                    {leadDocuments.length === 0 ? (
                      <Card className="border-dashed p-8">
                        <EmptyState
                          icon={FileText}
                          title="No files uploaded yet"
                          description="Upload documents related to this client's lead history."
                        />
                      </Card>
                    ) : (
                      <ScrollArea className="h-[400px] pr-4">
                        <div className="space-y-3">
                          {leadDocuments.map((document: any) => (
                            <Card key={document._id} className="p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <FileText className="h-5 w-5 text-gray-400" />
                                  <div>
                                    <p className="font-medium">{document.fileName}</p>
                                    <p className="text-sm text-gray-500">
                                      {(document.fileSize / 1024).toFixed(1)} KB •{" "}
                                      {document.fileType}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button variant="ghost" size="sm">
                                    <Download className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm">
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </TabsContent>
                </Tabs>
              </Card>
            </div>
          )}
        </div>
        <EditClientSheet
          isOpen={isEditSheetOpen}
          onClose={() => setIsEditSheetOpen(false)}
          client={business}
        />
        <AddLocationSheet
          isOpen={isAddLocationSheetOpen}
          onClose={() => setIsAddLocationSheetOpen(false)}
          businessId={id as Id<"businesses">}
        />
        {selectedLocation && (
          <EditLocationSheet
            isOpen={isEditLocationSheetOpen}
            onClose={() => setIsEditLocationSheetOpen(false)}
            location={selectedLocation}
          />
        )}
        {selectedBidForAttachment && id && (
          <AttachToLocationSheet
            isOpen={isAttachToLocationSheetOpen}
            onClose={() => setIsAttachToLocationSheetOpen(false)}
            bidId={selectedBidForAttachment._id}
            businessId={id as Id<"businesses">}
            currentLocationId={selectedBidForAttachment.serviceLocationId}
            onSuccess={() => {
              // Refresh the bids list by closing and reopening
              setIsAttachToLocationSheetOpen(false);
            }}
          />
        )}
      </DashboardLayout>
    </APIProvider>
  );
}
